import React, { Fragment, useContext, useEffect, useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { Chip, makeStyles, Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
// noinspection ES6CheckImport
import {
	MuiAppContext,
	MuiAssetGallery,
	MuiFormatPrice,
	MuiLink,
	MuiLoremIpsum,
} from '@lsoft/mui';
import { getTranslation, Tr } from '@lsoft/translate';
import CardHeader from '@material-ui/core/CardHeader';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import { ShopProductStockInfo } from './ShopProductStockInfo';
import TableBody from '@material-ui/core/TableBody';
import { ShopSingleProductStyles } from './ShopSingleProduct.styles';
import Hidden from '@material-ui/core/Hidden';
import { ProductLabels } from './ProductLabels';
import {
	trArticleNumber,
	trDiscoverSimilarProducts,
	trEAN,
	trIncludingVatPlusShippingCosts,
	trManufacturer,
	trManufacturerPartNumber,
	trPreviousPrice,
	trProductDescription,
	trProductDetails,
} from './Product.tr';
import { ProductCartAddButton } from '../ProductCartAddButton';
import { useRouter } from 'next/router';
import { ProductContext } from '@lsoft/shared/container/Product';
import { GroupTagsContext } from '@lsoft/shared/container/pim/GroupTags';
import { ShopProductContext } from '../../container/shop/ShopProduct';
import { usePathname } from 'next/navigation';

const useStyles = makeStyles((theme) => ({
	...ShopSingleProductStyles(theme),
}));

const ShopSingleProductGallery = ({ theme }) => {
	const classes = useStyles({ theme });
	// noinspection JSCheckFunctionSignatures
	const {
		data: { assets, asset_index },
	} = useContext(ProductContext);

	const sorted_pictures = useMemo(() => {
		return asset_index.product_pictures.map((uid) => {
			return assets[uid];
		});
	}, [asset_index, assets]);

	return (
		<Card className={clsx(classes.shopSingleProductAssetGallery)}>
			<CardContent>
				<MuiAssetGallery assets={sorted_pictures}>
					<Box>
						<MuiAssetGallery.Image magnify />
					</Box>
					<Hidden smDown initialWidth={'sm'}>
						<Box>
							<MuiAssetGallery.Selector />
						</Box>
					</Hidden>
				</MuiAssetGallery>
			</CardContent>
		</Card>
	);
};

const ShopSingleProductTeaser = ({ theme }) => {
	const classes = useStyles({ theme });
	// noinspection JSCheckFunctionSignatures
	const {
		data: { name_text, short_text, mname },
	} = useContext(ProductContext);

	return (
		<Box className={clsx(classes.shopSingleProductTeaser)}>
			<Grid
				container
				direction={'row'}
				justifyContent={'flex-start'}
				spacing={1}
				className={clsx(classes.shopSingleProductManufacturer)}
			>
				<Grid item>
					<Typography variant={'subtitle1'}>{mname}</Typography>
				</Grid>
			</Grid>
			<Typography
				className={clsx(classes.shopSingleProductHeadline)}
				component={'h1'}
				variant={'h4'}
				gutterBottom
			>
				<Tr tr={name_text} />
			</Typography>
			<Grid container spacing={2}>
				<Grid item>
					<ProductLabels hide_presale_labels />
				</Grid>
			</Grid>
			{!_.isNil(short_text) && (
				<Typography variant={'subtitle1'}>
					<Tr tr={short_text} />
				</Typography>
			)}
		</Box>
	);
};

const ShopSingleProductPrice = () => {
	const {
		data: {
			discount_available,
			real_brutto_sp,
			brutto_sp,
			brutto_dp,
			currency,
		},
	} = useContext(ProductContext);

	return (
		<Fragment>
			{discount_available && (
				<Typography
					variant="caption"
					component="div"
					style={{ marginBottom: 8 }}
				>
					<Tr tr={trPreviousPrice} />
					:&nbsp;
					<del>
						<MuiFormatPrice currency={currency} value={brutto_sp} />
					</del>
				</Typography>
			)}
			<Typography variant={'h4'} component={'p'} color={'primary'}>
				<MuiFormatPrice currency={currency} value={real_brutto_sp} />
			</Typography>
			<Typography variant={'caption'}>
				<Tr tr={trIncludingVatPlusShippingCosts} />
			</Typography>
		</Fragment>
	);
};

const ShopSingleProductOffer = ({ theme }) => {
	const classes = useStyles({ theme });
	// noinspection JSCheckFunctionSignatures
	const {
		data: { sellable, stock_info_variant },
	} = useContext(ShopProductContext);

	return (
		<Grid container direction={'column'} spacing={3}>
			<Grid item className={clsx(classes.shopSingleProductOfferInfo)}>
				<ShopSingleProductPrice />
			</Grid>
			<Grid item className={clsx(classes.shopSingleProductOfferInfo)}>
				<ShopProductStockInfo
					variant={stock_info_variant}
					titleVariant={'h6'}
				/>
			</Grid>
			{sellable && (
				<Grid item>
					<ProductCartAddButton />
				</Grid>
			)}
		</Grid>
	);
};

const ShopSingleProductOfferCard = () => {
	return (
		<Card>
			<CardContent>
				<ShopSingleProductTeaser />
				<ShopSingleProductOffer />
			</CardContent>
		</Card>
	);
};

const ShopSingleProductDescriptionCard = ({ theme }) => {
	const classes = useStyles(theme);
	const {
		data: { long_text },
	} = useContext(ProductContext);
	return (
		<Card className={clsx(classes.shopSingleProductDescription)}>
			<CardHeader title={<Tr tr={trProductDescription} />} />
			<CardContent>
				<Tr tr={long_text} html />
			</CardContent>
		</Card>
	);
};

const ShopSingleProductTagCard = () => {
	return (
		<Card>
			<CardHeader title={<Tr tr={trDiscoverSimilarProducts} />} />
			<CardContent>
				<ShopSingleProductTags />
			</CardContent>
		</Card>
	);
};

const ShopSingleProductDetails = ({ theme }) => {
	const classes = useStyles({ theme });
	// noinspection JSCheckFunctionSignatures
	const {
		data: { sku, mname, msku, gtin },
	} = useContext(ProductContext);
	// noinspection JSValidateTypes
	return (
		<Card className={clsx(classes.shopSingleProductDetails)}>
			<CardHeader title={<Tr tr={trProductDetails} />} />
			<CardContent>
				<TableContainer component={Box}>
					<Table size={'small'}>
						<TableBody>
							<TableRow>
								<TableCell>
									<Tr tr={trArticleNumber} html />
								</TableCell>
								<TableCell>{sku}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>
									<Tr tr={trManufacturer} />
								</TableCell>
								<TableCell>{mname}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>
									<Tr tr={trManufacturerPartNumber} />
								</TableCell>
								<TableCell>{msku}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>
									<Tr tr={trEAN} />
								</TableCell>
								<TableCell>{gtin}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</CardContent>
		</Card>
	);
};

function ShopSingleProductOfferDetailsScroller() {
	// const { scrollRef } = useContext(MuiAppContext);
	// const [topPosition, setTopPosition] = useState(0);
	//
	// const handleScroll = () => {
	// 	if (scrollRef.current.scrollTop <= scrollRef.current.scrollHeight * 0.9) {
	// 		setTopPosition(scrollRef.current.scrollTop);
	// 	}
	// };
	//
	// useEffect(() => {
	// 	if (!_.isNil(scrollRef)) {
	// 		const scrollRefCurrent = _.get(scrollRef, 'current', null);
	// 		if (!_.isNil(scrollRefCurrent)) {
	// 			scrollRefCurrent.addEventListener('scroll', handleScroll);
	// 			return () => {
	// 				scrollRefCurrent.removeEventListener('scroll', handleScroll);
	// 			};
	// 		}
	// 	}
	// }, [scrollRef]);

	return (
		<Grid item xs={12} md={5} lg={4}>
			<Box style={{ position: 'relative' }}>
				<Box
				// onScroll={() => handleScroll()}
				// style={{ position: 'absolute', top: topPosition }}
				>
					<Grid container direction={'row'} spacing={3}>
						<Grid item xs={12}>
							<Card>
								<ShopSingleProductOfferCard />
							</Card>
						</Grid>
						<Grid item xs={12}>
							<ShopSingleProductTagCard />
						</Grid>
						<Grid item xs={12}>
							<ShopSingleProductDetails />
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Grid>
	);
}

const ShopSingleProductTags = () => {
	const { locale } = useRouter();
	const { data: group_tags_data } = useContext(GroupTagsContext);

	const chips = Object.values(group_tags_data).map((group_tag, index) => {
		return (
			<Grid item key={index}>
				<MuiLink href={'/group?grouptags=' + group_tag.uid} fullWidth>
					<Chip
						color={'primary'}
						label={getTranslation(group_tag.name, locale)}
						clickable
						size={'small'}
					/>
				</MuiLink>
			</Grid>
		);
	});

	return (
		<Grid
			container
			spacing={1}
			direction={'row'}
			alignItems={'stretch'}
			justifyContent={'flex-start'}
			component={'nav'}
		>
			{chips}
		</Grid>
	);
};

export const ShopSingleProduct = () => {
	const { scrollTop } = useContext(MuiAppContext);
	const pathname = usePathname();
	useEffect(() => {
		console.log('ShopSingleProduct useEffect');
		// scrollTop();
	}, []);
	return (
		<Grid container direction={'row'} spacing={3}>
			<Grid item xs={12} md={7} lg={8}>
				<Grid container direction={'row'} spacing={3}>
					<Grid item xs={12}>
						{/*<MuiLoremIpsum />*/}
						<ShopSingleProductGallery />
					</Grid>
					<Hidden mdUp initialWidth={'xs'}>
						<Grid item xs={12}>
							<ShopSingleProductOfferCard />
						</Grid>
					</Hidden>
					<Hidden mdUp initialWidth={'xs'}>
						<Grid item xs={12}>
							<ShopSingleProductTagCard />
						</Grid>
					</Hidden>
					<Grid item xs={12}>
						<ShopSingleProductDescriptionCard />
					</Grid>
					<Hidden mdUp initialWidth={'xs'}>
						<Grid item xs={12}>
							<ShopSingleProductDetails />
						</Grid>
					</Hidden>
				</Grid>
			</Grid>
			<Hidden smDown initialWidth={'md'}>
				<ShopSingleProductOfferDetailsScroller />
			</Hidden>
		</Grid>
	);
};
